<script lang="ts">
	import type { ItineraryResultsStoreContent } from '$lib/components/itinerary/types';
	import { browser } from '$app/environment';
	import { page as pageState } from '$app/state';
	import { page } from '$app/stores';
	import PartnerPanel from '$lib/components/advertising/partners/card/PartnerPanel.svelte';
	import { getMapLayoutContext } from '$lib/components/map/map-layout-context';
	import Costs from '$lib/components/route-details/Costs.svelte';
	import CostsSkeleton from '$lib/components/route-details/CostsSkeleton.svelte';
	import ExpenseNote from '$lib/components/route-details/expense-note/ExpenseNote.svelte';
	import ExpenseNoteSkeleton from '$lib/components/route-details/expense-note/ExpenseNoteSkeleton.svelte';
	import { getAdvertiserData } from '$lib/constants/advertising';
	import { ROUTE_COSTS_EDIT } from '$lib/constants/route';
	import { track } from '$lib/services/tracking/googleAnalytics';
	import {
		trackAdsTextLineCtaClick,
		trackAdsTextLineDisplay,
		trackPartnerCardClicked,
		trackPartnerCardLoaded,
	} from '$lib/services/tracking/googleAnalytics/partners';
	import { buildItineraryTrackingData, trackItinerary } from '$lib/services/tracking/googleAnalytics/tracking';
	import { getSearchParamsStore, gotoWithParams } from '$lib/services/urls/searchParams';

	$: ({ itineraryResultsStore } = $page.data);
	const advertiserConfiguration = getAdvertiserData();
	const searchParams = getSearchParamsStore();
	const { itineraryStore, mapController, isMapLoaded } = getMapLayoutContext();
	const { itinerary } = itineraryStore;

	$: route
		= $searchParams.selectedRoute !== undefined
			? $itineraryResultsStore?.routes[$searchParams.selectedRoute]
			: undefined;

	function handleEditCar() {
		track({
			action: 'itinerary_edit',
			payload: {
				action: 'edit',
				...buildItineraryTrackingData(pageState),
			},
		});

		gotoWithParams('/routes', $searchParams);
	}

	function getAdsTextLineTrackingParams(advertiser: string) {
		return {
			advertiser,
			searchParams: $searchParams,
			route: $itineraryResultsStore?.routes[$searchParams.selectedRoute ?? 0],
			waypoints: $itineraryResultsStore?.waypoints,
			arrivalAddress: $itineraryResultsStore?.arrivalAddress,
			departureAddress: $itineraryResultsStore?.departureAddress,
		};
	}

	function getAddress(address: 'departure' | 'arrival') {
		if (!$itinerary?.length) {
			return '';
		}

		const index = address === 'departure' ? 0 : $itinerary.length - 1;

		if ($itinerary[index].isMyLocation) {
			const name = $itineraryResultsStore?.waypoints?.[index]?.name;
			const city = $itineraryResultsStore?.waypoints?.[index]?.city;

			return `${name ?? ''}${name && city ? ', ' : ''}${city}`;
		}

		return $itinerary[index].label ?? '';
	}

	function handleEditExpenseNote() {
		trackItinerary('itinerary_edit');

		gotoWithParams(ROUTE_COSTS_EDIT, { ...$searchParams });
	}

	$: if ($itineraryResultsStore?.routes.length && $isMapLoaded) {
		$mapController.fitCoordinates(
			$itineraryResultsStore.routes.flatMap((route: ItineraryResultsStoreContent['routes'][0]) => route.geometry.coordinates),
		);
	}
</script>

<div class="pb-2 md:pb-4 flex flex-col gap-2">
	{#if $itineraryResultsStore?.fetching}
		<CostsSkeleton />
	{:else if route}
		<Costs
			costs={route.costs}
			totalCost={route.totalCost}
			tolls={route.tolls}
			vignettes={route.vignettes}
			car={$searchParams.car}
			{...advertiserConfiguration?.adsTextLine}
			on:editCar={handleEditCar}
			on:mountAdTextLine={({ detail: advertiser }) => {
				trackAdsTextLineDisplay(getAdsTextLineTrackingParams(advertiser));
			}}
			on:ctaClickAdTextLine={({ detail: advertiser }) => {
				trackAdsTextLineCtaClick(getAdsTextLineTrackingParams(advertiser));
			}}
		/>
		{#if browser}
			<PartnerPanel {route} trackOnClick={trackPartnerCardClicked} trackOnLoad={trackPartnerCardLoaded} class="my-0" />
		{/if}
	{/if}
	{#if $itineraryResultsStore?.fetching}
		<ExpenseNoteSkeleton />
	{:else if route}
		<ExpenseNote
			{route}
			from={getAddress('departure')}
			to={getAddress('arrival')}
			locale={$page.data.locale}
			onEditExpenseNote={handleEditExpenseNote}
		/>
	{/if}
</div>
